/**
 * Advisory WCAG 2.x contrast pairs for the rebrand editor: which editable
 * (or contrast-derived) theme vars are measured against each other, at which
 * AA threshold. Purely informational — nothing validates against this on
 * save; custom org/tenant palettes may legitimately fail pairs and still be
 * applied (the badge just reports it).
 */
export type ContrastCheckKind = 'text' | 'ui';

export interface ThemeContrastCheck {
  /** varName of the foreground (text/icon/control) color */
  foreground: string;
  /** varName of the background it renders on */
  background: string;
  /** 'text' → SC 1.4.3 (AA 4.5:1); 'ui' → SC 1.4.11 (AA 3:1) */
  kind: ContrastCheckKind;
}

/** WCAG AA minimum contrast ratio per check kind. */
export const CONTRAST_THRESHOLDS: Record<ContrastCheckKind, number> = {
  text: 4.5,
  ui: 3,
};

const SURFACE_BACKGROUNDS = [
  '--mfw-palette-background-default',
  '--mfw-palette-background-paper',
  '--mfw-palette-background-neutral',
  '--mfw-palette-background-table-0',
  '--mfw-palette-background-table-1',
];

const onAllSurfaces = (
  foreground: string,
  kind: ContrastCheckKind,
): ThemeContrastCheck[] =>
  SURFACE_BACKGROUNDS.map((background) => ({ foreground, background, kind }));

export const THEME_CONTRAST_CHECKS: readonly ThemeContrastCheck[] = [
  ...onAllSurfaces('--mfw-palette-text-primary', 'text'),
  ...onAllSurfaces('--mfw-palette-text-secondary', 'text'),
  ...onAllSurfaces('--mfw-palette-text-icon', 'ui'),
  ...onAllSurfaces('--mfw-palette-action-active', 'ui'),
  {
    foreground: '--mfw-palette-primary-contrastText',
    background: '--mfw-palette-primary-main',
    kind: 'text',
  },
  // The same pair for the status families. They do NOT rebrand, but a super
  // admin sets them per tenant, so the label has to be graded against whatever
  // fill that tenant chose — not just against today's default.
  ...(['success', 'error', 'warning'] as const).map((family) => ({
    foreground: `--mfw-palette-${family}-contrastText`,
    background: `--mfw-palette-${family}-main`,
    kind: 'text' as const,
  })),
  {
    foreground: '--mfw-palette-primary-main',
    background: '--mfw-palette-background-default',
    kind: 'ui',
  },
  {
    foreground: '--mfw-palette-primary-main',
    background: '--mfw-palette-background-paper',
    kind: 'ui',
  },
  {
    foreground: '--mfw-palette-primary-contrastText',
    background: '--mfw-palette-accent-main',
    kind: 'text',
  },
];
